import type {PayloadAction} from '@reduxjs/toolkit';
import {createSelector, createSlice} from '@reduxjs/toolkit';
import type {PosterObject, PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';
import type {CopyableItemStyles, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {AiState, AiVideosState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AiImageState, AiSubtitleState, AiVideoGenerationStatus, AiVoiceState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import type {SuggestionItems} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.types';
import type {RootState} from '@/store';
import type {DeepPartial} from '@/global';
import type {FancyTextItemObject} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {FeatureDemo} from '@Components/poster-editor/poster-editor.types';
import {MobilePageSelectionModeState} from '@PosterWhiteboard/poster/poster.types';
import type {UserSaveConflictPopupProps} from '@Components/user-save-conflict-popup';
import type {PosterVersionData} from '@Components/poster-editor/library/poster-editor-library';
import {getPersistedIsOnMyDesignExpanded} from '@Components/poster-editor/library/poster-editor-storage-library';
import {isPageVideo as isPageVideoUtil} from '@Libraries/page-object.library';
import {computePageDuration, getPageDuration} from '@Libraries/page-duration.library';
import type {ItemLoadingProgressType, ItemLoadingReason} from '@PosterWhiteboard/items/item/item.types';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import type {PageItemsObject} from '@PosterWhiteboard/page/page-items.class';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import type {LayerEntry} from '@Components/poster-editor/components/layers-panel/layers-panel.types';
import {LayerType} from '@Components/poster-editor/components/layers-panel/layers-panel.types';
import {areLayerEntriesEqual} from '@Components/poster-editor/components/layers-panel/layers-panel.helper';
import {shallowEqual} from 'react-redux';
import type {SelectedTimelineItem} from '@Components/poster-editor/components/timeline/timeline.types';
import {TimelineItemType} from '@Components/poster-editor/components/timeline/timeline.types';

export interface UpdatePosterDataProps {
  posterObject?: PosterObject;
  hasUndo?: boolean;
  hasUnsavedChanges?: boolean;
  hasRedo?: boolean;
  isVideo?: boolean;
  currentPageId?: string;
  activeItemIds?: string[];
  isPosterVideo?: boolean;
}

interface UpdateIsDesktopTimelineToggleOnProps {
  isDesktopTimelineToggleOn: boolean;
  pageIdToScrollToOnWhiteboardResize?: string;
}

interface UpdatePageDataPayload {
  pageHashId: string;
  obj: DeepPartial<PageObject>;
}

interface UpdateItemDataPayload {
  pageHashId: string;
  itemHashId: string;
  obj: DeepPartial<ItemObject>;
}

interface UpdateAudioItemIsPlayingPayload {
  audioItemUID: string;
  isPlaying: boolean;
}

interface UpdateArePosterItemsModifyingProps {
  pageHashedId: string;
  isModifying: boolean;
}

export interface UpdateShouldHideControlsProps {
  activeItemHashId: string;
  shouldHideControls: boolean;
}

interface UpdateActiveItemsOnPagePayload {
  pageHashedId: string;
  activeItemIds: string[];
}

interface UpdateHoveredItemProps {
  pageHashedId: string;
  hoveredItemId: string | undefined;
}

interface UpdateMarqueePreviewItemsPayload {
  pageHashedId: string;
  itemIds: string[];
}

export interface ItemLoadingState {
  isVisible: boolean;
  progress: number;
  langTextKeys?: string[];
  progressType?: ItemLoadingProgressType;
  reason?: ItemLoadingReason;
}

interface UpdateItemLoadingStatePayload {
  pageHashedId: string;
  itemId: string;
  state: ItemLoadingState | null;
}

interface StateProps {
  posterObject: PosterObject | null;
  hasUndo: boolean;
  isPosterSaving: boolean;
  didPosterSaveFailed: boolean;
  arePosterItemsMoving: Record<string, boolean>;
  arePosterItemsScaling: Record<string, boolean>;
  arePosterItemsRotating: Record<string, boolean>;
  shouldHideControls: Record<string, boolean>;
  didPosterRecentlySave: boolean;
  hasUnsavedChanges: boolean;
  hasRedo: boolean;
  isVideo: boolean;
  currentPageId: string;
  isEmbeddedExportMode: boolean;
  activeItemIds: Record<string, string[]>;
  hoveredItemId: Record<string, string>;
  // Items on a page that the in-progress marquee box currently covers — the set that a mouseup
  // would select. Rendered as hover boxes so the user can see what the marquee will grab.
  marqueePreviewItemIds: Record<string, string[]>;
  isPosterVideo?: boolean;
  isSpellCheckSettingsActive?: boolean;
  isSidebarOpen: boolean;
  isSidebarSmall?: boolean;
  isMobileVariant?: boolean;
  isPickingModeEnabled?: boolean;
  isRemoveBGModeEnabled?: boolean;
  aiState: AiState;
  copiedStyles?: CopyableItemStyles;
  isUploadingToMyUploads: boolean;
  isPublishDropDownOpen: boolean;
  inputSuggestions?: SuggestionItems;
  businessAddressSuggestions?: SuggestionItems;
  showAutoSaveTooltip: boolean;
  haveShownAutoSaveTooltip: boolean;
  isPosterOverlayLoaderActive: boolean;
  isVersionHistoryOverlayActive: boolean;
  versionHistorySelectedVersion: PosterVersionData | null;
  currentUserId?: number;
  triggerOpenTemplatesPopup?: boolean;
  triggerOpenMyDesignsPopup?: boolean;
  featureToShowOnLoad?: FeatureToShowOnLoad;
  featureDemo?: FeatureDemo;
  triggerOpenRecordOptions?: boolean;
  isPosterShared?: boolean;
  isPosterSharedInTeam?: boolean;
  saveConflictData?: SaveConflictData;
  isVersionHistoryModeActive: boolean;
  isDesktopTimelineToggleOn?: boolean;
  fitToScreenOnWhiteboardResize: boolean;
  pageIdToScrollToOnWhiteboardResize?: string;
  itemLoadingStates: Record<string, Record<string, ItemLoadingState>>;
  mobilePageSelectionModeState: MobilePageSelectionModeState;
  pagePreviews: Record<string, string>;
  triggerOpenDownloadWebPanel?: boolean;
  isPosterInitialized: boolean;
  isOnMyDesignExpanded: boolean;
  openAnimationPanelRequested?: boolean;
  closeAnimationPanelRequested?: boolean;
  isAnimationPanelActiveInSidebar?: boolean;
  isLayersPanelOpen: boolean;
  hasShownOnboardingPopup: boolean;
}

interface SaveConflictData extends UserSaveConflictPopupProps {
  triggerOpenSaveConflictPopup: boolean;
}

const initialState: StateProps = {
  hasUndo: false,
  isPosterSaving: false,
  didPosterSaveFailed: false,
  arePosterItemsMoving: {},
  arePosterItemsScaling: {},
  arePosterItemsRotating: {},
  shouldHideControls: {},
  didPosterRecentlySave: false,
  hasRedo: false,
  hasUnsavedChanges: false,
  isVideo: false,
  isEmbeddedExportMode: false,
  currentPageId: '',
  hasShownOnboardingPopup: false,
  posterObject: null,
  activeItemIds: {},
  hoveredItemId: {},
  marqueePreviewItemIds: {},
  isMobileVariant: isEditorMobileVariant(),
  isPosterVideo: false,
  isSpellCheckSettingsActive: false,
  isSidebarOpen: true,
  isSidebarSmall: false,
  isPickingModeEnabled: false,
  isRemoveBGModeEnabled: false,
  aiState: {
    aiImage: AiImageState.IDLE,
    aiSubtitles: AiSubtitleState.IDLE,
    aiVoice: AiVoiceState.IDLE,
    aiVideo: [],
    aiCredits: null,
  },
  isUploadingToMyUploads: false,
  isPublishDropDownOpen: false,
  showAutoSaveTooltip: false,
  haveShownAutoSaveTooltip: false,
  copiedStyles: undefined,
  isPosterOverlayLoaderActive: false,
  isVersionHistoryOverlayActive: false,
  versionHistorySelectedVersion: null,
  triggerOpenTemplatesPopup: false,
  triggerOpenMyDesignsPopup: false,
  triggerOpenRecordOptions: false,
  featureDemo: FeatureDemo.NONE,
  isPosterShared: false,
  isPosterSharedInTeam: false,
  saveConflictData: undefined,
  isDesktopTimelineToggleOn: false,
  fitToScreenOnWhiteboardResize: false,
  pageIdToScrollToOnWhiteboardResize: undefined,
  isVersionHistoryModeActive: false,
  itemLoadingStates: {},
  mobilePageSelectionModeState: MobilePageSelectionModeState.INACTIVE,
  pagePreviews: {},
  triggerOpenDownloadWebPanel: false,
  isPosterInitialized: false,
  isOnMyDesignExpanded: getPersistedIsOnMyDesignExpanded(),
  openAnimationPanelRequested: false,
  closeAnimationPanelRequested: false,
  isAnimationPanelActiveInSidebar: false,
  isLayersPanelOpen: false,
};

const posterEditorSlice = createSlice({
  name: 'posterEditor',
  initialState,
  reducers: {
    updatePosterData: (state, action: PayloadAction<UpdatePosterDataProps>) => {
      if (action.payload.posterObject !== undefined) {
        state.posterObject = action.payload.posterObject;
      }
      if (action.payload.hasUndo !== undefined) {
        state.hasUndo = action.payload.hasUndo;
      }
      if (action.payload.hasUnsavedChanges !== undefined) {
        state.hasUnsavedChanges = action.payload.hasUnsavedChanges;
      }
      if (action.payload.currentPageId !== undefined) {
        state.currentPageId = action.payload.currentPageId;
      }
      if (action.payload.hasRedo !== undefined) {
        state.hasRedo = action.payload.hasRedo;
      }
      if (action.payload.isVideo !== undefined) {
        state.isVideo = action.payload.isVideo;
      }
      if (action.payload.isPosterVideo !== undefined) {
        state.isPosterVideo = action.payload.isPosterVideo;
      }
    },
    setPosterOverlayLoaderVisibility: (state, action: PayloadAction<boolean>) => {
      state.isPosterOverlayLoaderActive = action.payload;
    },
    setHasShownOnboardingPopup: (state, action: PayloadAction<boolean>) => {
      state.hasShownOnboardingPopup = action.payload;
    },
    setVersionHistoryOverlayVisibility: (state, action: PayloadAction<boolean>) => {
      state.isVersionHistoryOverlayActive = action.payload;
    },
    setVersionHistorySelectedVersion: (state, action: PayloadAction<PosterVersionData | null>) => {
      state.versionHistorySelectedVersion = action.payload;
    },
    restoreInitialData: (state) => {
      state.didPosterSaveFailed = initialState.didPosterSaveFailed;
      state.arePosterItemsScaling = initialState.arePosterItemsScaling;
      state.arePosterItemsMoving = initialState.arePosterItemsMoving;
      state.arePosterItemsRotating = initialState.arePosterItemsRotating;
      state.didPosterRecentlySave = initialState.didPosterRecentlySave;
      state.activeItemIds = initialState.activeItemIds;
      state.hoveredItemId = initialState.hoveredItemId;
      state.marqueePreviewItemIds = initialState.marqueePreviewItemIds;
      state.shouldHideControls = initialState.shouldHideControls;
      state.fitToScreenOnWhiteboardResize = initialState.fitToScreenOnWhiteboardResize;
      state.pageIdToScrollToOnWhiteboardResize = initialState.pageIdToScrollToOnWhiteboardResize;
      state.isPosterInitialized = initialState.isPosterInitialized;
      state.isLayersPanelOpen = initialState.isLayersPanelOpen;
    },
    updateHasUndo: (state, action: PayloadAction<boolean>) => {
      state.hasUndo = action.payload;
    },
    updateIsPosterSaving: (state, action: PayloadAction<boolean>) => {
      state.isPosterSaving = action.payload;
    },
    updateDidPosterSaveFailed: (state, action: PayloadAction<boolean>) => {
      state.didPosterSaveFailed = action.payload;
    },
    updateArePosterItemsMoving: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsMoving[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateArePosterItemsScaling: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsScaling[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateArePosterItemsRotating: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsRotating[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateShouldHideControls: (state, action: PayloadAction<UpdateShouldHideControlsProps>) => {
      state.shouldHideControls[action.payload.activeItemHashId] = action.payload.shouldHideControls;
    },
    onPosterItemsModified: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.arePosterItemsMoving[action.payload];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.arePosterItemsScaling[action.payload];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.arePosterItemsRotating[action.payload];
    },
    updateItemData: (state, action: PayloadAction<UpdateItemDataPayload>) => {
      if (!state.posterObject) return;

      const {pageHashId, itemHashId, obj} = action.payload;
      const item = state.posterObject.pages.pagesHashMap[pageHashId]?.items.itemsHashMap[itemHashId];

      if (!item) return;

      const updates: Partial<typeof item> = {};
      if (obj.left !== undefined) updates.left = obj.left;
      if (obj.top !== undefined) updates.top = obj.top;
      if (obj.scaleX !== undefined) updates.scaleX = obj.scaleX;
      if (obj.scaleY !== undefined) updates.scaleY = obj.scaleY;
      if (obj.angle !== undefined) updates.angle = obj.angle;

      Object.assign(item, updates);
    },
    updatePageData: (state, action: PayloadAction<UpdatePageDataPayload>) => {
      if (!state.posterObject) return;

      const {pageHashId, obj} = action.payload;
      const page = state.posterObject.pages.pagesHashMap[pageHashId];

      if (!page) return;

      const updates: Partial<PageObject> = {};
      if (obj.isPlaying !== undefined) updates.isPlaying = obj.isPlaying;
      if (obj.currentTime !== undefined) updates.currentTime = obj.currentTime;

      Object.assign(page, updates);
    },
    updateDidPosterRecentlySave: (state, action: PayloadAction<boolean>) => {
      state.didPosterRecentlySave = action.payload;
    },
    updateIsEmbeddedExportMode: (state, action: PayloadAction<boolean>) => {
      state.isEmbeddedExportMode = action.payload;
    },
    updateHasRedo: (state, action: PayloadAction<boolean>) => {
      state.hasRedo = action.payload;
    },
    updateIsPosterInitialized: (state, action: PayloadAction<boolean>) => {
      state.isPosterInitialized = action.payload;
    },
    updateIsPosterEditorMobileVariant: (state, action: PayloadAction<boolean>) => {
      state.isMobileVariant = action.payload;
    },
    updateActiveItemsOnPage: (state, action: PayloadAction<UpdateActiveItemsOnPagePayload>) => {
      state.activeItemIds[action.payload.pageHashedId] = action.payload.activeItemIds;
    },
    updateHoveredItem: (state, action: PayloadAction<UpdateHoveredItemProps>) => {
      if (action.payload.hoveredItemId) {
        state.hoveredItemId[action.payload.pageHashedId] = action.payload.hoveredItemId;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.hoveredItemId[action.payload.pageHashedId];
      }
    },
    updateMarqueePreviewItems: (state, action: PayloadAction<UpdateMarqueePreviewItemsPayload>) => {
      if (action.payload.itemIds.length > 0) {
        state.marqueePreviewItemIds[action.payload.pageHashedId] = action.payload.itemIds;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.marqueePreviewItemIds[action.payload.pageHashedId];
      }
    },
    updateItemLoadingState: (state, action: PayloadAction<UpdateItemLoadingStatePayload>) => {
      const {pageHashedId, itemId, state: loadingState} = action.payload;

      if (loadingState) {
        if (!state.itemLoadingStates[pageHashedId]) {
          state.itemLoadingStates[pageHashedId] = {};
        }
        state.itemLoadingStates[pageHashedId][itemId] = loadingState;
      } else {
        if (state.itemLoadingStates[pageHashedId]) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete state.itemLoadingStates[pageHashedId][itemId];
          if (Object.keys(state.itemLoadingStates[pageHashedId]).length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete state.itemLoadingStates[pageHashedId];
          }
        }
      }
    },
    setSelectedAudioItemUID: (state, action: PayloadAction<string>) => {
      if (state.posterObject) {
        const currentPage = state.posterObject.pages.pagesHashMap[state.currentPageId];
        if (currentPage) {
          currentPage.audioClips.selectedAudioItemUID = action.payload;
        }
      }
    },
    updateAudioItemIsPlaying: (state, action: PayloadAction<UpdateAudioItemIsPlayingPayload>) => {
      if (state.posterObject) {
        const currentPage = state.posterObject.pages.pagesHashMap[state.currentPageId];
        if (currentPage?.audioClips.audioItemsHashMap[action.payload.audioItemUID]) {
          currentPage.audioClips.audioItemsHashMap[action.payload.audioItemUID].audioPlayer.isPlaying = action.payload.isPlaying;
        }
      }
    },
    updateSpellCheckSettingsState: (state, action: PayloadAction<boolean>) => {
      state.isSpellCheckSettingsActive = action.payload;
    },
    updateSidebarState: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },
    updateCollapsableSidebarState: (state, action: PayloadAction<boolean>) => {
      state.isSidebarSmall = action.payload;
    },
    updatePickingMode: (state, action: PayloadAction<boolean>) => {
      state.isPickingModeEnabled = action.payload;
    },
    updateRemoveBgMode: (state, action: PayloadAction<boolean>) => {
      state.isRemoveBGModeEnabled = action.payload;
    },
    updateAiImageState: (state, action: PayloadAction<AiImageState>) => {
      state.aiState.aiImage = action.payload;
    },
    updateAiSubtitleState: (state, action: PayloadAction<AiSubtitleState>) => {
      state.aiState.aiSubtitles = action.payload;
    },
    updateAiVoiceState: (state, action: PayloadAction<AiVoiceState>) => {
      state.aiState.aiVoice = action.payload;
    },
    addAiVideoGenerationItem: (state, action: PayloadAction<AiVideosState>) => {
      state.aiState.aiVideo.unshift(action.payload);
    },
    markAiVideoGenerationFailed: (state, action: PayloadAction<{id: string; errorCode: string | undefined}>) => {
      const item = state.aiState.aiVideo.find((i) => i.id === action.payload.id);
      if (item) {
        item.status = AiVideoGenerationStatus.ERROR;
        item.errorCode = action.payload.errorCode;
      }
    },
    removeAiVideoGenerationItem: (state, action: PayloadAction<string>) => {
      state.aiState.aiVideo = state.aiState.aiVideo.filter((item) => item.id !== action.payload);
    },
    setAiCredits: (state, action: PayloadAction<number>) => {
      state.aiState.aiCredits = action.payload;
    },
    updateIsUploadingToMyUploads: (state, action: PayloadAction<boolean>) => {
      if (state.isUploadingToMyUploads !== action.payload) {
        state.isUploadingToMyUploads = action.payload;
      }
    },
    updatePublishDropDownState: (state, action: PayloadAction<boolean>) => {
      state.isPublishDropDownOpen = action.payload;
    },
    setInputSuggestions: (state, action: PayloadAction<SuggestionItems>) => {
      state.inputSuggestions = action.payload;
    },
    setBusinessAddress: (state, action: PayloadAction<SuggestionItems>) => {
      state.businessAddressSuggestions = action.payload;
    },
    updateAutoSaveTooltipState: (state, action: PayloadAction<boolean>) => {
      state.showAutoSaveTooltip = action.payload;
      if (action.payload) {
        state.haveShownAutoSaveTooltip = true;
      }
    },
    updateCopiedStylesState: (state, action: PayloadAction<CopyableItemStyles | undefined>) => {
      state.copiedStyles = action.payload;
    },
    updateCurrentUserId: (state, action: PayloadAction<number>) => {
      state.currentUserId = action.payload;
    },
    updateFeatureToShowOnLoad: (state, action: PayloadAction<FeatureToShowOnLoad>) => {
      state.featureToShowOnLoad = action.payload;
    },
    updateFeatureDemo: (state, action: PayloadAction<FeatureDemo>) => {
      state.featureDemo = action.payload;
    },
    updateRecordOptionsOpenState: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenRecordOptions = action.payload;
    },
    updateIsPosterShared: (state, action: PayloadAction<boolean>) => {
      state.isPosterShared = action.payload;
    },
    updateIsPosterSharedInTeam: (state, action: PayloadAction<boolean>) => {
      state.isPosterSharedInTeam = action.payload;
    },
    updateSaveConflictDataDetails: (state, action: PayloadAction<SaveConflictData | undefined>) => {
      state.saveConflictData = action.payload;
    },
    updateVersionHistoryModeState: (state, action: PayloadAction<boolean>) => {
      state.isVersionHistoryModeActive = action.payload;
    },
    updatePageIdToScrollToOnWhiteboardResize: (state, action: PayloadAction<string | undefined>) => {
      state.pageIdToScrollToOnWhiteboardResize = action.payload;
    },
    updateFitToScreenOnWhiteboardResize: (state, action: PayloadAction<boolean>) => {
      state.fitToScreenOnWhiteboardResize = action.payload;
    },
    updateIsDesktopTimelineToggleOn: (state, action: PayloadAction<UpdateIsDesktopTimelineToggleOnProps>) => {
      state.isDesktopTimelineToggleOn = action.payload.isDesktopTimelineToggleOn;
      state.fitToScreenOnWhiteboardResize = true;
      state.pageIdToScrollToOnWhiteboardResize = action.payload.pageIdToScrollToOnWhiteboardResize;
    },
    updatePosterScale: (state, action: PayloadAction<number>) => {
      if (state.posterObject) {
        state.posterObject.scaling.scale = action.payload;
      }
    },
    setMobilePageSelectionModeState: (state, action: PayloadAction<MobilePageSelectionModeState>) => {
      state.mobilePageSelectionModeState = action.payload;
    },
    updatePagePreview: (state, action: PayloadAction<{pageHashedId: string; previewUrl: string}>) => {
      state.pagePreviews[action.payload.pageHashedId] = action.payload.previewUrl;
    },
    removePagePreview: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.pagePreviews[action.payload];
    },
    updateWebDownloadPanelOpenState: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenDownloadWebPanel = action.payload;
    },
    updateIsOnMyDesignExpanded: (state, action: PayloadAction<boolean>) => {
      state.isOnMyDesignExpanded = action.payload;
    },
    updateTriggerOpenTemplatesPopup: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenTemplatesPopup = action.payload;
    },
    updateTriggerOpenMyDesignsPopup: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenMyDesignsPopup = action.payload;
    },
    requestOpenAnimationPanel: (state) => {
      state.openAnimationPanelRequested = true;
      state.isSidebarOpen = true;
    },
    clearOpenAnimationPanelRequest: (state) => {
      state.openAnimationPanelRequested = false;
    },
    requestCloseAnimationPanel: (state) => {
      state.closeAnimationPanelRequested = true;
    },
    clearCloseAnimationPanelRequest: (state) => {
      state.closeAnimationPanelRequested = false;
    },
    setIsAnimationPanelActiveInSidebar: (state, action: PayloadAction<boolean>) => {
      state.isAnimationPanelActiveInSidebar = action.payload;
    },
    updateLayersPanelState: (state, action: PayloadAction<boolean>) => {
      state.isLayersPanelOpen = action.payload;
    },
  },
});

export const {
  updatePosterData,
  setPosterOverlayLoaderVisibility,
  setHasShownOnboardingPopup,
  setVersionHistoryOverlayVisibility,
  setVersionHistorySelectedVersion,
  restoreInitialData,
  updateAudioItemIsPlaying,
  updateIsEmbeddedExportMode,
  setSelectedAudioItemUID,
  updateActiveItemsOnPage,
  updateHoveredItem,
  updateMarqueePreviewItems,
  updateItemLoadingState,
  updateHasUndo,
  updateDidPosterRecentlySave,
  updateIsPosterSaving,
  updateDidPosterSaveFailed,
  updateArePosterItemsMoving,
  updateArePosterItemsRotating,
  updateArePosterItemsScaling,
  updateShouldHideControls,
  onPosterItemsModified,
  updateItemData,
  updatePageData,
  updateHasRedo,
  updateSpellCheckSettingsState,
  updateSidebarState,
  updateCollapsableSidebarState,
  updatePickingMode,
  updateRemoveBgMode,
  updateIsPosterEditorMobileVariant,
  updateAiImageState,
  updateAiSubtitleState,
  updateAiVoiceState,
  addAiVideoGenerationItem,
  markAiVideoGenerationFailed,
  removeAiVideoGenerationItem,
  setAiCredits,
  updateIsUploadingToMyUploads,
  updatePublishDropDownState,
  setInputSuggestions,
  setBusinessAddress,
  updateCopiedStylesState,
  updateAutoSaveTooltipState,
  updateCurrentUserId,
  updateRecordOptionsOpenState,
  updateFeatureToShowOnLoad,
  updateFeatureDemo,
  updateIsPosterShared,
  updateIsPosterSharedInTeam,
  updateSaveConflictDataDetails,
  updateVersionHistoryModeState,
  updatePageIdToScrollToOnWhiteboardResize,
  updateFitToScreenOnWhiteboardResize,
  updateIsDesktopTimelineToggleOn,
  updatePosterScale,
  setMobilePageSelectionModeState,
  updatePagePreview,
  removePagePreview,
  updateWebDownloadPanelOpenState,
  updateIsPosterInitialized,
  requestOpenAnimationPanel,
  clearOpenAnimationPanelRequest,
  requestCloseAnimationPanel,
  clearCloseAnimationPanelRequest,
  setIsAnimationPanelActiveInSidebar,
  updateTriggerOpenTemplatesPopup,
  updateTriggerOpenMyDesignsPopup,
  updateIsOnMyDesignExpanded,
  updateLayersPanelState,
} = posterEditorSlice.actions;
export const posterEditorReducer = posterEditorSlice.reducer;

export const getSelectedAudioItem = createSelector(
  [
    (state: RootState): Record<string, AudioItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips.audioItemsHashMap;
    },
    (state: RootState): string | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips.selectedAudioItemUID;
    },
  ],
  (audioItemHashMap, selectedAudioItemUID) => {
    if (!audioItemHashMap || !selectedAudioItemUID) {
      return undefined;
    }

    return audioItemHashMap[selectedAudioItemUID];
  }
);

export const getAudioItemFromCurrentPageById = createSelector(
  [
    (state: RootState): Record<string, AudioItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips?.audioItemsHashMap;
    },
    (_state: RootState, audioItemId: string): string => audioItemId,
  ],
  (audioItemsHashMap, audioItemId) => {
    if (!audioItemsHashMap || !audioItemId) {
      return undefined;
    }
    return audioItemsHashMap[audioItemId];
  }
);

export const getSelectedAudioItemHasFade = createSelector([getSelectedAudioItem], (audioItem): boolean => {
  if (!audioItem) {
    return false;
  }
  return audioItem.audioPlayer.fade.fadeInDuration !== 0 || audioItem.audioPlayer.fade.fadeOutDuration !== 0;
});

export const getPosterObject = (state: RootState): PosterObject | null => {
  return state.posterEditor.posterObject;
};

export const getCopiedStyles = (state: RootState): CopyableItemStyles | undefined => {
  return state.posterEditor.copiedStyles;
};

export const selectItem = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap;
    },
    (_state: RootState, itemId: string): string => itemId,
  ],
  (pages, itemId) => {
    if (!pages) {
      return undefined;
    }

    for (const [, page] of Object.entries(pages)) {
      if (page.items.itemsHashMap[itemId]) {
        return page.items.itemsHashMap[itemId];
      }
    }

    return undefined;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const isPagePlaying = (state: RootState, pageId: string): boolean => {
  return state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.isPlaying ?? false;
};

export const getIsCurrentPagePlaying = (state: RootState): boolean => {
  const currentPageId = state.posterEditor.currentPageId;
  return state.posterEditor.posterObject?.pages?.pagesHashMap[currentPageId]?.isPlaying ?? false;
};

export const isPagePlaybackEnded = createSelector(
  [
    (state: RootState, pageId: string): number | undefined => {
      const page = state.posterEditor.posterObject?.pages?.pagesHashMap[pageId];
      return page ? getPageDuration(page) : undefined;
    },
    (state: RootState, pageId: string): number => state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.currentTime ?? 0,
    (_state: RootState, pageId: string): string => pageId,
  ],
  (pageDuration, currentTime) => {
    if (pageDuration === undefined) {
      return false;
    }

    return Math.abs(currentTime - pageDuration) < PAGE_END_TIME_TOLERANCE_SECONDS;
  }
);

export const getPagePlaybackIcon = createSelector(
  [
    (state: RootState, pageId: string): boolean => isPagePlaying(state, pageId),
    (_state: RootState, pageId: string): string => pageId,
  ],
  (playing) => {
    if (playing) {
      return 'icon-pause';
    }

    return 'icon-play-video';
  }
);

export const getCurrentPageDuration = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const id = state.posterEditor.currentPageId;
      return id ? state.posterEditor.posterObject?.pages.pagesHashMap[id]?.items : undefined;
    },
    (state: RootState): AudioClipsObject | undefined => {
      const id = state.posterEditor.currentPageId;
      return id ? state.posterEditor.posterObject?.pages.pagesHashMap[id]?.audioClips : undefined;
    },
  ],
  (items, audioClips): number | undefined => {
    if (!items) {
      return undefined;
    }
    return computePageDuration(items, audioClips);
  }
);

export const getCurrentPageTime = (state: RootState): number => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return 0;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.currentTime ?? 0;
};

export const getCurrentPageTimeFloored = (state: RootState): number => {
  return Math.floor(getCurrentPageTime(state));
};

export const getCurrentPageTimeRounded = (state: RootState): number => {
  return Math.round(getCurrentPageTime(state));
};

/**
 * The single timeline item currently selected: the active canvas item (except transcripts) or the selected audio clip.
 * Inputs are playback-stable references/primitives and the result is shallow-compared, so the returned
 * object identity survives playback ticks and unrelated store actions.
 */
// The single "selected timeline entity" read, derived entirely from the model: canvas
// objects (graphic items + backgrounds) from the fabric selection (activeItemIds), audio
// from audioClips.selectedAudioItemUID. Transcripts are skipped (their subtitles are the
// timeline-track selection, tracked on transcript.activeSubtitleId). Returns the
// {type, id} shape every timeline consumer reads.
export const getSelectedTimelineItem = createSelector(
  [
    (state: RootState): ItemObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      const activeItemIds = state.posterEditor.activeItemIds[currentPageId];
      if (activeItemIds?.length !== 1) {
        return undefined;
      }
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.items?.itemsHashMap?.[activeItemIds[0]];
    },
    (state: RootState): string | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId]?.audioClips?.selectedAudioItemUID;
    },
  ],
  (selectedItem, selectedAudioUID): SelectedTimelineItem | undefined => {
    if (selectedItem) {
      if (selectedItem.gitype === ITEM_TYPE.TRANSCRIPT) {
        return undefined;
      }
      return {type: selectedItem.isBackground ? TimelineItemType.BACKGROUND : TimelineItemType.ITEM, id: selectedItem.uid};
    }
    if (selectedAudioUID) {
      return {type: TimelineItemType.AUDIO, id: selectedAudioUID};
    }
    return undefined;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getPageCurrentTime = (state: RootState, pageId: string): number => {
  if (!pageId) {
    return 0;
  }
  return state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.currentTime ?? 0;
};

export const getPosterDuration = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap;
    },
  ],
  (pages) => {
    if (!pages) {
      return undefined;
    }

    let totalDuration = 0;
    for (const [, page] of Object.entries(pages)) {
      totalDuration += getPageDuration(page);
    }

    return totalDuration;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

// Returns the bg whose [onPageStartTime, onPageEndTime) contains the page's current time.
// Mirrors PageItems.getActiveBackground — picks the latest-starting match so that during
// a transition overlap the incoming bg wins.
export const getCurrentPageBackground = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return currentPageId ? state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items : undefined;
    },
    (state: RootState): number => {
      const currentPageId = state.posterEditor.currentPageId;
      return currentPageId ? state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.currentTime ?? 0 : 0;
    },
  ],
  (items, currentTime): ItemObject | undefined => {
    if (!items) {
      return undefined;
    }
    for (let i = items.itemsOrder.length - 1; i >= 0; i--) {
      const item = items.itemsHashMap[items.itemsOrder[i]];
      if (item?.isBackground && currentTime >= item.onPageStartTime && currentTime < item.onPageEndTime) {
        return item;
      }
    }
    return undefined;
  }
);

export const getCurrentPageBackgroundsInOrder = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return currentPageId ? state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items : undefined;
    },
  ],
  (items): ItemObject[] => {
    if (!items) {
      return [];
    }
    const bgs: ItemObject[] = [];
    for (const uid of items.itemsOrder) {
      const item = items.itemsHashMap[uid];
      if (item?.isBackground) {
        bgs.push(item);
      }
    }
    bgs.sort((a, b) => a.onPageStartTime - b.onPageStartTime);
    return bgs;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getPageBackgroundByUid = (state: RootState, pageId: string, backgroundUid: string): ItemObject | undefined => {
  const item = state.posterEditor.posterObject?.pages.pagesHashMap?.[pageId]?.items.itemsHashMap[backgroundUid];
  return item?.isBackground ? item : undefined;
};

// The background that is the current single active selection, or undefined when
// nothing (or something else) is selected. No playhead fallback — use this to tell
// whether the user is editing a background by selecting it.
export const getSelectedBackground = (state: RootState): ItemObject | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  const activeIds = state.posterEditor.activeItemIds[currentPageId];
  if (activeIds?.length === 1) {
    const selected = state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap[activeIds[0]];
    if (selected?.isBackground) {
      return selected;
    }
  }
  return undefined;
};

// The background the user is editing: the selected background when a single
// background is the active selection, otherwise the background under the
// playhead. Lets the background panels target a background by identity so an
// edit stays on it while the clock moves, instead of following the playhead.
export const getBackgroundBeingEdited = (state: RootState): ItemObject | undefined => {
  return getSelectedBackground(state) ?? getCurrentPageBackground(state);
};


export const getCurrentPageHashedID = (state: RootState): string | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.hashedID;
};


export const getCurrentPageItems = (state: RootState): PageItemsObject | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
};

export const getCurrentPageAppliedPalette = (state: RootState): ColorPaletteInfo | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.appliedPalette;
};

export const getNumberOfPages = (state: RootState): number => {
  if (!state.posterEditor.posterObject?.pages.pagesHashMap) {
    return 0;
  }
  return Object.keys(state.posterEditor.posterObject.pages.pagesHashMap).length;
};

export const getIsPosterInitialized = (state: RootState): boolean => {
  return state.posterEditor.isPosterInitialized;
};

export const getIsOnMyDesignExpanded = (state: RootState): boolean => {
  return state.posterEditor.isOnMyDesignExpanded;
};

export const getHasShownOnboardingPopup = (state: RootState): boolean => {
  return state.posterEditor.hasShownOnboardingPopup;
};

export const isScaleAnimatingToFitToScreen = createSelector(
  [
    (state: RootState): boolean => {
      return state.posterEditor.posterObject?.scaling.isScaleAnimating === true;
    },
    (state: RootState): boolean => {
      return state.posterEditor.posterObject?.scaling.isCurrentScaleBestFit === true;
    },
  ],
  (isScaleAnimating, isCurrentScaleBestFit) => {
    return isScaleAnimating && isCurrentScaleBestFit;
  }
);

export const getNumberOfItemsInCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
  ],
  (itemsHashMap) => {
    if (!itemsHashMap) {
      return 0;
    }

    return Object.keys(itemsHashMap).length;
  }
);

export const getAllTranscriptItemObjects = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages.pagesHashMap;
    },
  ],
  (pages) => {
    if (!pages) {
      return {};
    }
    const transcriptItemObjectsHashmap: Record<string, TranscriptItemObject> = {};
    for (const page of Object.values(pages)) {
      for (const item of Object.values(page.items.itemsHashMap)) {
        if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
          transcriptItemObjectsHashmap[item.uid] = item as TranscriptItemObject;
        }
      }
    }

    return transcriptItemObjectsHashmap;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getTranscriptItemsInCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
  ],
  (itemsHashMap): TranscriptItemObject[] => {
    if (!itemsHashMap) {
      return [];
    }
    return Object.values(itemsHashMap).filter((item): item is TranscriptItemObject => item.gitype === ITEM_TYPE.TRANSCRIPT);
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getAllSelectedItemObjectsOnCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
    (state: RootState): string[] | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.activeItemIds[currentPageId];
    },
  ],
  (itemsHashMap, selectedIdsOnPage) => {
    if (!itemsHashMap || !selectedIdsOnPage || selectedIdsOnPage.length === 0) {
      return {};
    }

    const selectedItemObjects: Record<string, ItemObject> = {};
    for (const id of selectedIdsOnPage) {
      const item = itemsHashMap[id];
      if (item) {
        selectedItemObjects[id] = item;
      }
    }

    return selectedItemObjects;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

/**
 * returns item object if it is the only item selected otherwise returns null
 */
export const getSingleActiveItemObjectOnPage = (state: RootState, pageId?: string): ItemObject | null => {
  const activeItemIdsOnPage = state.posterEditor.activeItemIds[pageId ?? state.posterEditor.currentPageId];
  if (activeItemIdsOnPage?.length !== 1) {
    return null;
  }

  return state.posterEditor.posterObject?.pages.pagesHashMap[pageId ?? state.posterEditor.currentPageId].items.itemsHashMap[activeItemIdsOnPage[0]] ?? null;
};

export const getCurrentPageVideoItemsWithAudioInOrder = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
    },
  ],
  (items): VideoItemObject[] => {
    if (!items) {
      return [];
    }

    const videoItems: VideoItemObject[] = [];

    for (const itemId of items.itemsOrder) {
      const item = items.itemsHashMap[itemId];
      if (item.gitype === ITEM_TYPE.VIDEO) {
        videoItems.push(item as VideoItemObject);
      }
    }

    return videoItems;
  }
);

// returns audio items in order of their onPageStartTime (using name as a tie-breaker)
export const getAudioItemsInOrder = createSelector(
  [
    (state: RootState): AudioClipsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips;
    },
  ],
  (audioClipsObject): AudioItemObject[] => {
    const audioItems: AudioItemObject[] = [];
    if (audioClipsObject) {
      audioItems.push(...Object.values(audioClipsObject.audioItemsHashMap));
    }

    audioItems.sort((a, b) => {
      if (a.onPageStartTime !== b.onPageStartTime) {
        return a.onPageStartTime - b.onPageStartTime;
      }
      return a.name.localeCompare(b.name);
    });

    return audioItems;
  }
);

export const getItemTrackItemsByTimelineSubRow = createSelector(
  [getCurrentPageItems],
  (pageItems): ItemObject[] => {
    if (!pageItems) {
      return [];
    }

    const items: ItemObject[] = [];
    for (const itemId of pageItems.itemsOrder) {
      const item = pageItems.itemsHashMap[itemId];
      if (item && item.gitype !== ITEM_TYPE.TRANSCRIPT && !item.isBackground) {
        items.push(item);
      }
    }

    items.sort((a, b) => a.timelineSubRow - b.timelineSubRow);

    return items;
  }
);

export const getTextItems = createSelector(
  [
    (state: RootState): PosterPageObject | undefined => {
      return state.posterEditor.posterObject?.pages;
    },
  ],
  (pages): (TextItemObject | FancyTextItemObject)[] => {
    if (!pages) return [];

    return Object.values(pages.pagesHashMap).flatMap((page) => {
      const items = Object.values(page.items.itemsHashMap);

      return items.filter((item): item is TextItemObject | FancyTextItemObject => {
        return item.gitype === ITEM_TYPE.TEXT || item.gitype === ITEM_TYPE.FANCY_TEXT;
      });
    });
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

const selectAiVideos = (state: RootState): AiVideosState[] => state.posterEditor.aiState.aiVideo;

export const selectGeneratingAiVideos = createSelector([selectAiVideos], (aiVideo): AiVideosState[] => aiVideo.filter((item) => item.status === AiVideoGenerationStatus.GENERATING), {
  memoizeOptions: {resultEqualityCheck: shallowEqual},
});

export const selectFailedAiVideos = createSelector([selectAiVideos], (aiVideo): AiVideosState[] => aiVideo.filter((item) => item.status === AiVideoGenerationStatus.ERROR), {
  memoizeOptions: {resultEqualityCheck: shallowEqual},
});

export const selectGeneratingAiVideoCount = createSelector([selectGeneratingAiVideos], (generating): number => generating.length);

export const selectIsAnyAiVideoGenerating = createSelector([selectGeneratingAiVideos], (generating): boolean => generating.length > 0);

export const selectHasFailedAiVideo = createSelector([selectFailedAiVideos], (failed): boolean => failed.length > 0);

export const selectPendingAiVideoRetry = (state: RootState): AiVideosState | null =>
  state.posterEditor.aiState.aiVideo.find((item) => item.status === AiVideoGenerationStatus.PENDING) ?? null;

export const selectAiCredits = (state: RootState): number => state.posterEditor.aiState.aiCredits ?? 0;

export const getActivityIndicatorTypeForAi = createSelector(
  [(state: RootState): AiState => state.posterEditor.aiState, selectIsAnyAiVideoGenerating, selectHasFailedAiVideo],
  (aiState, hasGeneratingVideo, hasErroredVideo): ActivityIndicatorType | undefined => {
    if (aiState.aiImage === AiImageState.GENERATING || aiState.aiSubtitles === AiSubtitleState.GENERATING || hasGeneratingVideo) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiState.aiSubtitles === AiSubtitleState.READY_FAILED || hasErroredVideo) {
      return ActivityIndicatorType.ERROR;
    }
    if (aiState.aiImage === AiImageState.READY || aiState.aiSubtitles === AiSubtitleState.READY_SUCCESS) {
      return ActivityIndicatorType.SUCCESS;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiImage = createSelector(
  [
    (state: RootState): AiImageState => {
      return state.posterEditor.aiState.aiImage;
    },
  ],
  (aiImageStatus): ActivityIndicatorType | undefined => {
    if (aiImageStatus === AiImageState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiImageStatus === AiImageState.READY) {
      return ActivityIndicatorType.SUCCESS;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiSubtitle = createSelector(
  [
    (state: RootState): AiSubtitleState => {
      return state.posterEditor.aiState.aiSubtitles;
    },
  ],
  (aiSubtitleStatus): ActivityIndicatorType | undefined => {
    if (aiSubtitleStatus === AiSubtitleState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiSubtitleStatus === AiSubtitleState.READY_SUCCESS) {
      return ActivityIndicatorType.SUCCESS;
    }
    if (aiSubtitleStatus === AiSubtitleState.READY_FAILED) {
      return ActivityIndicatorType.ERROR;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiVoice = createSelector(
  [
    (state: RootState): AiVoiceState => {
      return state.posterEditor.aiState.aiVoice;
    },
  ],
  (aiVoiceStatus): ActivityIndicatorType | undefined => {
    if (aiVoiceStatus === AiVoiceState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiVoiceStatus === AiVoiceState.READY) {
      return ActivityIndicatorType.SUCCESS;
    }
    if (aiVoiceStatus === AiVoiceState.ERROR) {
      return ActivityIndicatorType.ERROR;
    }
    return undefined;
  }
);

export const isPageVideo = (state: RootState, pageId: string): boolean => {
  const page = state.posterEditor.posterObject?.pages?.pagesHashMap[pageId];
  if (!page) {
    return false;
  }
  return isPageVideoUtil(page);
};

/**
 * Raw user-controlled flag: did the user open desktop's timeline view?
 * Meaningless on mobile (mobile has no such toggle — the mobile timeline appears
 * automatically when on a video page). Prefer `selectIsTimelineVisible` or
 * `selectIsSinglePageLayout` unless you specifically need the desktop toggle.
 */
export const selectIsDesktopTimelineToggleOn = (state: RootState): boolean => {
  return state.posterEditor.isDesktopTimelineToggleOn === true;
};

/**
 * Is the timeline (bottom bar on desktop, bottom panel on mobile) visible to the user?
 * Desktop: the user has toggled timeline mode on, and the page is a video.
 * Mobile: the page is a video (timeline is always shown automatically).
 */
export const selectIsTimelineVisible = (state: RootState): boolean => {
  if (state.posterEditor.isVersionHistoryModeActive) {
    return false;
  }
  if (state.posterEditor.mobilePageSelectionModeState === MobilePageSelectionModeState.ACTIVE) {
    return false;
  }
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId || !isPageVideo(state, currentPageId)) {
    return false;
  }
  return state.posterEditor.isMobileVariant === true || selectIsDesktopTimelineToggleOn(state);
};

/**
 * Is the whiteboard laid out in single-page (zoomed) mode rather than scrolling through pages?
 * True on mobile (always) and on desktop when the timeline toggle is on.
 */
export const selectIsSinglePageLayout = (state: RootState): boolean => {
  return state.posterEditor.isMobileVariant === true || selectIsDesktopTimelineToggleOn(state);
};

export const selectIsCurrentPageModifying = (state: RootState): boolean => {
  const pageId = state.posterEditor.currentPageId;
  if (!pageId) {
    return false;
  }
  return state.posterEditor.arePosterItemsMoving[pageId] || state.posterEditor.arePosterItemsScaling[pageId] || state.posterEditor.arePosterItemsRotating[pageId];
};

export const selectLayerPanelRows = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
    },
  ],
  (items): LayerEntry[] => {
    if (!items) {
      return [];
    }
    const entries: LayerEntry[] = [];
    const memberUidsByGroupId = new Map<string, string[]>();
    const seenUids = new Set<string>();
    for (let i = items.itemsOrder.length - 1; i >= 0; i -= 1) {
      const uid = items.itemsOrder[i];
      const item = items.itemsHashMap[uid];
      if (!item || item.gitype === ITEM_TYPE.TRANSCRIPT || item.isBackground || seenUids.has(uid)) {
        continue;
      }
      seenUids.add(uid);
      if (item.groupId) {
        const existing = memberUidsByGroupId.get(item.groupId);
        if (existing) {
          existing.push(uid);
          continue;
        }
        const memberUids = [uid];
        memberUidsByGroupId.set(item.groupId, memberUids);
        entries.push({layerType: LayerType.Group, groupId: item.groupId, memberUids});
      } else {
        entries.push({layerType: LayerType.Item, uid});
      }
    }
    return entries;
  },
  {memoizeOptions: {resultEqualityCheck: areLayerEntriesEqual}}
);
